import type { ItemView } from "@fall26/shared";
import { useState } from "react";
import { useUpdateItem } from "../../api/hooks.js";

/**
 * A date, in one gesture, without opening the editor.
 *
 * This exists because of an asymmetry in where the dates come from. The Canvas
 * feed carries M E 130L and MKT 320F and nothing else, but 22 of the 23 undated
 * items belong to M E 335, M E 334 and M E 330 — courses that publish nothing.
 * No sync will ever resolve them. Typing a date by hand is the only path they
 * have, so it is the one that has to be quick.
 *
 * Tap, spin the native wheel, tap Save. The Save step is deliberate rather than
 * saving on change: a date input fires change on every spin of the wheel, so
 * committing on change would fire a write per digit and the last one to land
 * wins. Confirming also puts the chosen date in words on the button, which is
 * the difference between setting the 14th and setting the 4th.
 */
export function QuickDate({ item }: { item: ItemView }) {
  /*
   * Two different jobs behind one control.
   *
   * 20 of the 23 undated items have no date at all. The other 3 DO carry one
   * from the syllabus — it is just unconfirmed, so the row refuses to show it
   * rather than pass a provisional date off as a deadline. Opening an empty
   * picker for those three would throw away a date the app already knows and
   * make me look it up again, so the field is seeded with it and the button
   * says confirm rather than set.
   */
  const provisional = item.due.date;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(provisional ?? "");
  const update = useUpdateItem();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="tap tabular mt-1 inline-flex items-center gap-1.5 rounded-full border border-[var(--accent-line)] bg-[var(--accent-wash)] px-2.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--accent)]"
      >
        <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
          <rect
            x="1.5"
            y="2.5"
            width="9"
            height="8"
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M1.5 5h9M4 1.5v2M8 1.5v2"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
        {provisional ? "Confirm the date" : "Set a date"}
      </button>
    );
  }

  return (
    <div className="mt-1.5">
      {provisional && (
        <p className="tabular mb-1 text-[10.5px] text-[var(--text-subtle)]">
          Syllabus says {item.display.date} — confirm it or change it.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          type="date"
          autoFocus
          value={value}
          aria-label={`Due date for ${item.title}`}
          onChange={(event) => setValue(event.target.value)}
          className="tap min-w-0 flex-1 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-sunken)] px-2.5 text-[var(--text)] outline-none focus:border-[var(--accent)]"
        />
        <button
          type="button"
          disabled={value === "" || update.isPending}
          onClick={() =>
            update.mutate(
              {
                id: item.id,
                // dueDate alone, and sent even when the value is unchanged from
                // the provisional one. The server derives date_confirmed from
                // this field, so confirming a syllabus date IS a write — skipping
                // it as a no-op would leave the row in "needs a date" forever.
                patch: { dueDate: value as never },
                description: `Date "${item.title}" ${value}`,
              },
              { onSuccess: () => setOpen(false) },
            )
          }
          className="tap shrink-0 rounded-[var(--radius)] border border-[var(--text)] bg-[var(--text)] px-3 text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-inverted)] transition-opacity disabled:opacity-40"
        >
          {update.isPending ? "…" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="tap shrink-0 rounded-[var(--radius)] border border-[var(--border)] px-2.5 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
